// Minimal CSV helpers — no runtime dependency.
//
// Use:
//   const csv = toCSV(['pallet_id', 'net_weight_kg'], pallets, p => [p.pallet_id, p.net_weight_kg])
//   downloadCSV('run-2026-015-pallets.csv', csv)

// Canonical name for the cell-escape helper. Five inline copies live in
// page files (BinInventory / CagesReport / Estimates / EstimatesReport /
// StockReport) — all byte-identical to each other and all weaker than this
// one: they miss CR escaping and the formula-injection guard. New code
// MUST import csvCell from here. Migrate the inline copies opportunistically
// per REFACTOR-BACKLOG.md (Phase 4).
// A bare numeric literal — optional sign, digits, optional fraction, optional
// exponent. Nothing here can be a formula: a spreadsheet parses `-1234.5` as
// the number it looks like, and the injection vector needs a formula BODY
// (`-2+3+cmd|'/c calc'!A0`, `-SUM(A1)`), which this rejects.
const NUMERIC_LITERAL = /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/

// An Excel text-formula literal over DIGITS ONLY — what excelTextCell() below
// emits, and the only `=`-leading value csvCell lets through. Digits cannot
// form a formula body, so there is nothing for the injection guard to catch.
const EXCEL_TEXT_LITERAL = /^="\d+"$/

export function csvCell(v: unknown): string {
  if (v === null || v === undefined) return ''
  let s = String(v)
  // Let plain numbers through before the guard sees them. `\-` is in the guard's
  // character class, so EVERY negative number was shipped as `"'-1234.5"` — a
  // leading apostrophe is an explicit text marker, so the cell displayed
  // -1234.5 but was a STRING. SUM() then silently skipped every negative row:
  // a recon variance total or a cashflow net column added up too high by the
  // whole magnitude of the negatives, with nothing to show for it.
  if (NUMERIC_LITERAL.test(s)) return s
  // Force-quote an excelTextCell() wrapper and leave it evaluable. The guard
  // below would otherwise fire on its leading `=` and apostrophe-prefix it,
  // which is exactly what silently defeated the SSC fix on the grower report:
  // the operator saw the literal `'="17390000000012345"` where the SSC should
  // be. Digits-only, so it cannot smuggle a formula.
  if (EXCEL_TEXT_LITERAL.test(s)) return `"${s.replace(/"/g, '""')}"`
  // Formula-injection guard. Excel / LibreOffice / Google Sheets treat a
  // cell starting with =, +, -, @, TAB or CR as a formula. A workbook
  // opened in Excel with the cell `=cmd|'/c calc'!A1` runs arbitrary
  // logic the operator never typed. Prefix with a single-quote so the
  // value is treated as text, and force-quote so the prefix survives
  // the round-trip. See OWASP "CSV injection".
  if (/^[=+\-@\t\r]/.test(s)) {
    s = "'" + s
    return `"${s.replace(/"/g, '""')}"`
  }
  // Spec: wrap in double-quotes if the value contains comma, quote, newline
  // or carriage return; escape embedded quotes by doubling them.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Wrap a long numeric IDENTIFIER so a spreadsheet keeps every digit.
 *
 * Excel parses a bare run of digits as an IEEE-754 double and silently chops
 * anything past ~15 significant figures, so a 17/18-digit SSC comes out of the
 * file wrong — the 2026-06-02 operator report, "some SSC numbers are not
 * pulling through correctly". Excel's text-formula form `="…"` survives in
 * Excel and LibreOffice; Google Sheets strips the wrapper on import, which is
 * also correct. csvCell knows this shape and passes it through (see
 * EXCEL_TEXT_LITERAL) rather than neutralising the leading `=`.
 *
 * For identifiers only — anything non-numeric is returned unwrapped, since the
 * truncation this defends against cannot happen to it and the wrapper would
 * then be a formula the guard has to catch.
 */
export function excelTextCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const s = String(v)
  return /^\d+$/.test(s) ? `="${s}"` : s
}

export function toCSV<T>(headers: string[], rows: T[], mapper: (row: T) => unknown[]): string {
  const lines: string[] = []
  lines.push(headers.map(csvCell).join(','))
  for (const r of rows) {
    lines.push(mapper(r).map(csvCell).join(','))
  }
  return lines.join('\r\n')
}

export function downloadCSV(filename: string, body: string) {
  // ﻿ = BOM, so Excel opens UTF-8 correctly on Windows.
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Safe filename — replace anything other than [A-Za-z0-9._-] with an underscore.
export function safeFilename(s: string): string {
  return s.replace(/[^A-Za-z0-9._-]/g, '_')
}

// ---------------------------------------------------------------------------
// The truncation watermark.
//
// A register whose read is capped (the newest N rows) shows an honest banner on
// screen — but the banner cannot travel with the downloaded file, and the file
// is precisely what leaves the building. So when the cap was hit, the export
// carries the marker as its own final row, where a spreadsheet reader cannot
// miss it and a CSV parser round-trips it as one cell.
//
// A refusal would push the operator back to screenshots; a paged full refetch
// is a bigger change than an audit-honesty fix should carry. This is the
// generalisation of the spray-records original (spray-a-07/flow-A-14) — pass
// the cap and the two nouns rather than forking it per register.
// ---------------------------------------------------------------------------
export interface CapNotice {
  /** Rows the query returned — at the cap means it was truncated. */
  fetched:   number
  /** Rows the file actually holds, after the client-side filters. */
  exported:  number
  cap:       number
  /** What the register holds ("spray records", "observations"). */
  noun:      string
  /** What is MISSING, in the register's own words ("applications", "older
   *  observations") — reads better than repeating `noun` twice in one sentence. */
  olderNoun: string
  /** What to do instead, for a register with no date range to narrow
   *  (default: narrow the date range). Also drops "in the selected range". */
  hint?:     string
}

/** Non-null exactly when the fetch filled the cap. */
export function capNotice(n: CapNotice): string | null {
  if (n.fetched < n.cap) return null
  return `INCOMPLETE EXPORT — this file holds ${n.exported} row(s) filtered from only the most recent `
    + `${n.cap} ${n.noun}; older ${n.olderNoun}${n.hint ? '' : ' in the selected range'} are NOT included. `
    + (n.hint ?? 'Narrow the date range and export again for a complete register.')
}

/** Append the cap marker as the file's final row (single first-column cell). */
export function withCapNotice(body: string, n: CapNotice): string {
  const notice = capNotice(n)
  return notice == null ? body : `${body}\r\n${csvCell(notice)}`
}

// ---------------------------------------------------------------------------
// parseCSV — RFC-4180-ish reader. Handles double-quoted fields with embedded
// commas, escaped double-quotes ("" inside a quoted field), and either CRLF
// or LF line endings. Returns an array of objects keyed by the header row.
// Empty trailing lines are ignored.
//
// Sufficient for spreadsheet exports (Pastel, Excel, Google Sheets). Doesn't
// handle multi-line fields containing literal newlines inside quotes (rare
// for our inputs and would need a streaming tokeniser).
// ---------------------------------------------------------------------------
export function parseCSV(text: string): Record<string, string>[] {
  // Strip optional UTF-8 BOM that Excel/Pastel often prepend.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)

  const lines = text.split(/\r\n|\n/).filter(l => l.length > 0)
  if (lines.length === 0) return []

  const headers = splitCsvLine(lines[0])
  const rows: Record<string, string>[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i])
    const row: Record<string, string> = {}
    for (let j = 0; j < headers.length; j++) row[headers[j]] = cells[j] ?? ''
    rows.push(row)
  }
  return rows
}

// parseCSVGrid — the same reader as parseCSV, but it returns the raw rows
// (header row included) instead of objects keyed by the header. An import that
// lets the operator MAP columns needs the grid: the header row may not be the
// first line (a title above it), and two columns may share a heading.
//
// A CSV saved by Excel on a machine whose list separator is ';' (common where
// the decimal separator is a comma) is read with ';' — decided once, from the
// first non-blank line: semicolons and no commas.
export function parseCSVGrid(text: string): string[][] {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
  const lines = text.split(/\r\n|\n|\r/).filter(l => l.trim().length > 0)
  if (lines.length === 0) return []
  const first = lines[0]
  const sep = !first.includes(',') && first.includes(';') ? ';' : ','
  return lines.map(l => splitCsvLine(l, sep))
}

function splitCsvLine(line: string, sep = ','): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ }
        else inQuotes = false
      } else {
        cur += c
      }
    } else {
      if (c === sep)      { out.push(cur); cur = '' }
      else if (c === '"') { inQuotes = true }
      else                 { cur += c }
    }
  }
  out.push(cur)
  return out.map(s => s.trim())
}
